/**
 * Competitors module — mock widget loaders.
 *
 * Every widget reads `scoped(query).category`: all brands, chip-filtered —
 * the prototype's `CompetitorsView` rows (`canonicalQuery` keeps the `Brands`
 * selection on a 'competitors' page and prunes it everywhere else).
 */
import type { DashboardQuery, Selections, WidgetLoaders } from '@/data-sources/contract';
import type {
  BrandAuthors,
  BrandEngagementTotals,
  BrandMentions,
  BrandSentiment,
  BrandTopicSentiment,
} from '@/data-sources/contract/widgets/competitors';

import { aggregate } from '../engine/aggregate';
import { dimensionValues } from '../engine/dimensions';
import type { MentionRow } from '../engine/generator';
import { pivot } from '../engine/pivot';
import { standingsFor } from '../engine/standings';
import { applyChips, scoped } from '../query';
import { comparisonOf, timeSeriesOf } from './shared';

const brandMentions = (rows: readonly MentionRow[]): BrandMentions[] =>
  standingsFor(rows).map((s) => ({ brand: s.value, mentions: s.mentions }));

/** One brand's rows → its sentiment split, sentiments in chip order, empty ones omitted. */
function sentimentOf(brand: string, rows: readonly MentionRow[]): BrandSentiment {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.sentiment, (counts.get(row.sentiment) ?? 0) + 1);
  const known = dimensionValues.sentiment;
  const order = [...counts.keys()].sort((a, b) => known.indexOf(a) - known.indexOf(b));
  return {
    brand,
    sentiments: order.map((sentiment) => ({ sentiment, mentions: counts.get(sentiment)! })),
  };
}

/** Brands in chip order (the engine's `standingsFor` grouping). */
function brandSentiments(rows: readonly MentionRow[]): BrandSentiment[] {
  return standingsFor(rows).map((s) =>
    sentimentOf(
      s.value,
      rows.filter((row) => row.brand === s.value),
    ),
  );
}

/** Every brand × topic with a mention, by brand then topic in chip order. */
function brandTopicSentiments(rows: readonly MentionRow[]): BrandTopicSentiment[] {
  const groups = new Map<string, MentionRow[]>();
  for (const row of rows) {
    const key = `${row.brand}\u0000${row.topic}`;
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  const brands = dimensionValues.brand;
  const topics = dimensionValues.topic;
  return [...groups.values()]
    .map((group) => ({ ...sentimentOf(group[0]!.brand, group), topic: group[0]!.topic }))
    .sort(
      (a, b) =>
        brands.indexOf(a.brand) - brands.indexOf(b.brand) ||
        topics.indexOf(a.topic) - topics.indexOf(b.topic),
    );
}

/**
 * Each brand's authors. The engine names only the accounts with an audience
 * (`voice`) — the rest of the corpus is "thousands of ordinary accounts" — so a
 * named voice counts once however often it posts, and every other mention
 * stands for an author of its own.
 */
function brandAuthors(rows: readonly MentionRow[]): BrandAuthors[] {
  return standingsFor(rows).map((s) => {
    const mine = rows.filter((row) => row.brand === s.value);
    const voices = new Set(mine.flatMap((row) => (row.voice ? [row.voice] : [])));
    return { brand: s.value, authors: voices.size + mine.filter((row) => !row.voice).length };
  });
}

/** Each brand's mentions and the engagement on them. */
function brandEngagementTotals(rows: readonly MentionRow[]): BrandEngagementTotals[] {
  return standingsFor(rows).map((s) => {
    const mine = rows.filter((row) => row.brand === s.value);
    return {
      brand: s.value,
      mentions: mine.length,
      engagement: mine.reduce((sum, row) => sum + row.engagement, 0),
    };
  });
}

/** Share of voice's rows: `all` lifts the `Brands` selection, every other chip still applies. */
export function shareOfVoiceRows(query: DashboardQuery, category: 'chip' | 'all') {
  const s = scoped(query);
  if (category === 'chip') return { current: s.category, previous: s.categoryPrevious };
  const selections: Selections = { ...query.selections };
  delete selections.brand;
  return {
    current: applyChips(s.current, selections),
    previous: s.previous ? applyChips(s.previous, selections) : null,
  };
}

export const competitorsWidgetLoaders = {
  'share-of-voice': async ({ query, params }) => {
    const { current, previous } = shareOfVoiceRows(query, params.category);
    return {
      brands: brandMentions(current),
      previous: previous ? brandMentions(previous) : null,
    };
  },

  'competitor-sentiment-comparison': async ({ query }) => ({
    brands: brandSentiments(scoped(query).category),
  }),

  'share-of-positive': async ({ query }) => {
    const s = scoped(query);
    /* The prototype's bar order — `pivot`'s `sort: 'value'`, ties in first-seen order. */
    const positive = pivot(s.category, { metric: 'positiveShare', xBy: 'brand', sort: 'value' });
    const brands = positive.rows.map((_, index) => {
      const group = positive.rowsAt(index);
      return sentimentOf(group[0]!.brand, group);
    });
    return {
      brands,
      previous: s.categoryPrevious ? brandSentiments(s.categoryPrevious) : null,
    };
  },

  'share-of-voice-over-time': async ({ query }) => {
    const s = scoped(query);
    const spec = { metric: 'mentions', splitBy: 'brand' } as const;
    const volume = aggregate(s.category, { ...spec, from: s.from, to: s.to });
    const before = comparisonOf(s.categoryPrevious, s.previousBounds, volume, spec);
    return { volume: timeSeriesOf(volume, s.from, s.to, 'brand', before) };
  },

  'competitor-virality': async ({ query }) => {
    const s = scoped(query);
    const spec = { metric: 'reach', splitBy: 'brand' } as const;
    const reach = aggregate(s.category, { ...spec, from: s.from, to: s.to });
    const before = comparisonOf(s.categoryPrevious, s.previousBounds, reach, spec);
    return { reach: timeSeriesOf(reach, s.from, s.to, 'brand', before) };
  },

  'topic-ownership': async ({ query }) => ({
    rows: brandTopicSentiments(scoped(query).category),
  }),

  'mention-volume-vs-competitors': async ({ query }) => {
    const s = scoped(query);
    const spec = { metric: 'mentions', splitBy: 'brand' } as const;
    const volume = aggregate(s.category, { ...spec, from: s.from, to: s.to });
    const before = comparisonOf(s.categoryPrevious, s.previousBounds, volume, spec);
    return {
      volume: timeSeriesOf(volume, s.from, s.to, 'brand', before),
      yours: { current: s.own.length, previous: s.ownPrevious ? s.ownPrevious.length : null },
      category: {
        current: s.category.length,
        previous: s.categoryPrevious ? s.categoryPrevious.length : null,
      },
    };
  },

  'engagement-by-brand': async ({ query }) => {
    const s = scoped(query);
    return {
      brands: brandEngagementTotals(s.category),
      previous: s.categoryPrevious ? brandEngagementTotals(s.categoryPrevious) : null,
    };
  },

  'unique-authors-by-brand': async ({ query }) => {
    const s = scoped(query);
    return {
      brands: brandAuthors(s.category),
      previous: s.categoryPrevious ? brandAuthors(s.categoryPrevious) : null,
    };
  },
} satisfies Partial<WidgetLoaders>;
